import {
  AfterContentInit,
  Directive,
  ElementRef,
  HostListener,
  input,
  NgZone,
  OnChanges,
  OnDestroy,
  SimpleChanges,
} from '@angular/core';
import * as bootstrap from 'bootstrap';

@Directive({
  selector: '[libTooltip]',
  exportAs: 'lib-tooltip',
  standalone: false,
})
export class TooltipDirective
  implements AfterContentInit, OnChanges, OnDestroy
{
  libTooltip = input.required<string>();
  placement = input<bootstrap.Tooltip.PopoverPlacement>('top');
  triggers = input<bootstrap.Tooltip.Options['trigger'] | 'hover-persist'>(
    'hover focus',
  );
  containerClass = input<bootstrap.Tooltip.Options['customClass']>('');
  alwaysShow = input<boolean>(false);
  container = input<string | Element | false>(false);

  private tooltipObject?: bootstrap.Tooltip;
  private isShowing = false;

  private persistIsShowing = false;
  private persistSetup = false;

  constructor(
    private el: ElementRef,
    private zone: NgZone,
  ) {}

  ngAfterContentInit() {
    this.renderTooltip();
  }

  ngOnChanges(event: SimpleChanges): void {
    this.renderTooltip(event);
  }

  @HostListener('keydown', ['$event'])
  onKeydown(event: KeyboardEvent): void {
    if (this.triggers() !== 'click') return;
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      this.targetClickEvent();
    } else if (event.key === 'Escape' && this.isShowing) {
      this.hide();
      document.removeEventListener('click', this.documentClickEvent);
    }
  }

  @HostListener('focusout')
  onFocusout(): void {
    if (this.triggers() !== 'click' || !this.isShowing) return;
    this.hide();
    document.removeEventListener('click', this.documentClickEvent);
  }

  ngOnDestroy() {
    this.tooltipObject?.dispose();

    if (this.triggers() === 'hover-persist') {
      this.removeHoverPersistEvents();
    } else if (this.triggers() === 'click') {
      this.removeClickEvents();
    }
  }

  show() {
    if (!this.isShowing) {
      this.tooltipObject?.show();
      this.isShowing = true;
    }
  }

  hide() {
    if (this.isShowing) {
      this.tooltipObject?.hide();
      this.isShowing = false;
    }
  }

  private renderTooltip(event?: SimpleChanges): void {
    if (!this.libTooltip()) {
      this.hide();
      return;
    }

    this.el.nativeElement.setAttribute('aria-label', this.libTooltip());

    if (
      this.tooltipObject &&
      event?.libTooltip.currentValue === event?.libTooltip.previousValue
    ) {
      return;
    }
    if (!event?.libTooltip.firstChange) {
      const tooltip = bootstrap.Tooltip.getInstance(this.el.nativeElement);
      tooltip?.dispose();
    }
    const trigger = this.triggers();
    this.tooltipObject = new bootstrap.Tooltip(this.el.nativeElement, {
      placement: this.placement,
      trigger:
        trigger === 'hover-persist' || trigger === 'click'
          ? 'manual'
          : (trigger as bootstrap.Tooltip.Options['trigger']),
      customClass: this.containerClass() || '',
      html: true,
      title: this.libTooltip() || '',
      container: this.container(),
    });

    if (event?.libTooltip.firstChange) {
      if (this.triggers() === 'hover-persist') {
        this.addHoverPersistEvents();
      } else if (this.triggers() === 'click') {
        this.addClickEvents();
      }

      if (this.alwaysShow()) {
        this.show();
      }
    }
  }

  private addHoverPersistEvents() {
    this.el.nativeElement.addEventListener('mouseenter', this.mouseenterEvent);
    this.el.nativeElement.addEventListener('mouseleave', this.mouseleaveEvent);
    this.el.nativeElement.addEventListener('shown.bs.tooltip', this.shownEvent);
  }

  private removeHoverPersistEvents() {
    this.el.nativeElement.removeEventListener(
      'mouseenter',
      this.mouseenterEvent,
    );
    this.el.nativeElement.removeEventListener(
      'mouseleave',
      this.mouseleaveEvent,
    );
    this.el.nativeElement.removeEventListener(
      'shown.bs.tooltip',
      this.shownEvent,
    );
  }

  private addClickEvents() {
    const parent = this.el.nativeElement.parentElement;
    const targetElement = parent.classList.contains('tooltip-target-area')
      ? parent
      : this.el.nativeElement;

    targetElement.addEventListener('click', this.targetClickEvent);
  }

  private removeClickEvents() {
    const parent = this.el.nativeElement.parentElement;
    const targetElement = parent.classList.contains('tooltip-target-area')
      ? parent
      : this.el.nativeElement;

    targetElement.removeEventListener('click', this.targetClickEvent);
  }

  private persistHideTooltip() {
    this.persistIsShowing = false;
    this.zone.runOutsideAngular(() => {
      setTimeout(() => {
        if (!this.persistIsShowing) {
          this.hide();
          this.persistSetup = false;
        }
      }, 250);
    });
  }

  private persistShowTooltip() {
    this.persistIsShowing = true;
    this.show();
  }

  private mouseenterEvent = () => {
    this.persistShowTooltip();
  };

  private mouseleaveEvent = () => {
    this.persistHideTooltip();
  };

  private shownEvent = () => {
    if (this.persistSetup) {
      return;
    }
    const id = this.el.nativeElement.getAttribute('aria-describedby');
    const tooltipElement = document.querySelector(`#${id}`);

    if (!tooltipElement) {
      return;
    }

    tooltipElement.addEventListener('mouseleave', () => this.mouseleaveEvent());
    tooltipElement.addEventListener('mouseenter', () => this.mouseenterEvent());

    this.persistSetup = true;
  };

  private targetClickEvent = () => {
    if (this.isShowing) {
      this.hide();
      document.removeEventListener('click', this.documentClickEvent);
    } else {
      this.show();
      document.addEventListener('click', this.documentClickEvent);
    }
  };

  private documentClickEvent = ($event: MouseEvent) => {
    const parent = this.el.nativeElement.parentElement;
    const targetElement = parent.classList.contains('tooltip-target-area')
      ? parent
      : this.el.nativeElement;
    if (!this.tooltipObject) {
      return;
    }
    const id = this.el.nativeElement.getAttribute('aria-describedby');
    const tooltipElement = document.querySelector(`#${id}`);
    if (!tooltipElement) {
      return;
    }

    const clickedInside =
      tooltipElement.contains($event.target as Node) ||
      targetElement.contains($event.target);
    if (!clickedInside) {
      this.hide();
      document.removeEventListener('click', this.documentClickEvent);
    }
  };
}
